/**
 * Cloudflare Turnstile widget — invisible/managed CAPTCHA alternative.
 *
 * Loads the Cloudflare script once on mount, renders a widget into a
 * dedicated div, and emits the resulting token via onVerify. The token
 * is single-use and expires; the parent component should clear it
 * after submission and re-render this widget to get a fresh one.
 *
 * If VITE_TURNSTILE_SITE_KEY isn't set (local dev without Turnstile),
 * the component renders nothing and immediately calls onVerify("")
 * so the parent form stays usable.
 *
 * Widget mode is configured in the Cloudflare dashboard, not here —
 * we deliberately don't expose mode/theme props to keep call sites lean.
 */

import { useEffect, useRef } from "react";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          "timeout-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          appearance?: "always" | "execute" | "interaction-only";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  /** Called when token expires (~5 min). Parent should clear its stored token. */
  onExpire?: () => void;
}

let scriptLoadPromise: Promise<void> | null = null;

function ensureTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => reject(new Error("Turnstile script failed")), { once: true });
    document.head.appendChild(s);
  });

  return scriptLoadPromise;
}

export function TurnstileWidget({ onVerify, onExpire }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!SITE_KEY) {
      // Mock / unconfigured: immediately resolve with empty token so forms still submit.
      onVerify("");
      return;
    }
    if (!containerRef.current) return;

    let cancelled = false;
    const container = containerRef.current;

    ensureTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: SITE_KEY,
          callback: (token) => {
            if (!cancelled) onVerify(token);
          },
          "expired-callback": () => {
            if (!cancelled) {
              onVerify("");
              onExpire?.();
            }
          },
          "error-callback": () => {
            if (!cancelled) onVerify("");
          },
          theme: "auto",
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[Turnstile] failed to load", err);
        if (!cancelled) onVerify("");
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // tolerate races where the script unloaded first
        }
        widgetIdRef.current = null;
      }
    };
    // onVerify/onExpire are intentionally not in deps — we want a stable widget identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} className="cf-turnstile" />;
}

/** Helper so callers can branch their UI based on whether Turnstile is enabled. */
export const isTurnstileConfigured = Boolean(SITE_KEY);
